/**
 * Strategie — gedeelde API-laag en Rock-formulier voor de Strategie-pagina én de overlegpagina
 * (Rocks bewerken vanuit MT-punt, Joris 2 okt 2026). Muteren mag alleen een MT-lid met het recht
 * strategy:edit; de server controleert dat zelf ook (Node op main, de voordeur op develop).
 */
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { ROCK_STATUS } from './strategyTokens';

export const MT_EMAILS = new Set([
  'joris@fullframe.nl', 'vincent@fullframe.nl', 'joanne@fullframe.nl',
  'ton@fullframe.nl', 'pepijn@fullframe.nl',
]);

export const isMtLid = (email) => MT_EMAILS.has((email || '').trim().toLowerCase());

/** Zelfde rechten als de Strategie-pagina: MT-lid én strategy:edit. */
export const magStrategieBewerken = (email, permissions) => isMtLid(email) && !!permissions?.strategy?.edit;

const BASE = `${BACKEND_URL}/Api/strategy`;

async function vraag(pad, { method = 'GET', body, query } = {}) {
  const qs = query ? `?${new URLSearchParams(Object.fromEntries(Object.entries(query).filter(([, v]) => v != null && v !== '')))}` : '';
  const r = await authFetch(`${BASE}${pad}${qs}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(d.error || (typeof d.detail === 'string' ? d.detail : '') || `Fout ${r.status}`);
    fout.status = r.status;
    fout.body = d;
    throw fout;
  }
  return d;
}

/** Velden waarmee een verwijderde Rock opnieuw wordt aangemaakt (undo). */
export const ROCK_VELDEN = ['title', 'description', 'owner_email', 'pillar_id', 'status', 'progress_pct', 'deadline', 'sort_order', 'last_update_note'];
export const rockKopie = (rock) => Object.fromEntries(ROCK_VELDEN.filter((k) => rock?.[k] !== undefined).map((k) => [k, rock[k]]));

export const strategieApi = {
  lees: () => vraag(''),
  rockNieuw: (body) => vraag('/rocks', { method: 'POST', body }),
  rockWijzig: (id, body) => vraag(`/rocks/${id}`, { method: 'PATCH', body }),
  rockVerwijder: (id) => vraag(`/rocks/${id}`, { method: 'DELETE' }),
  /** acties: undefined | 'verplaatsen' (met naar) | 'verwijderen'. Bij actiepunten zonder keuze: fout.status 409, fout.body.code 'heeft_acties'. */
  propositieVerwijder: (id, { acties, naar } = {}) => vraag(`/propositions/${id}`, { method: 'DELETE', query: { acties, naar } }),
  propositieHerstel: (herstel) => vraag('/propositions/herstel', { method: 'POST', body: herstel }),
};

/** Eigenaar als keuzelijst uit de Personen-spiegel; een bestaande waarde die niet in de lijst staat blijft kiesbaar. */
export function eigenaarVeld(label, personen, huidig) {
  if (!personen?.length) return { name: 'owner_email', label: `${label} (email)`, type: 'text' };
  const opties = personen.map((p) => ({ value: p.email, label: p.naam || p.email }));
  if (huidig && !opties.some((o) => o.value === huidig)) opties.push({ value: huidig, label: huidig });
  return { name: 'owner_email', label, type: 'select', options: opties };
}

/** Formulier voor een Rock (StrategyEditModal-schema): titel, eigenaar, pijler, status, %, deadline (= kwartaal). */
export function rockSchema(pillars = [], personen = [], huidig) {
  const pillarOptions = (pillars || []).map((p) => ({ value: p.id, label: p.title }));
  return [
    { name: 'title', label: 'Titel', type: 'text', required: true },
    { name: 'description', label: 'Beschrijving', type: 'textarea', rows: 3 },
    eigenaarVeld('Eigenaar', personen, huidig),
    { name: 'pillar_id', label: 'Prioriteit', type: 'select', options: pillarOptions },
    {
      name: 'status', label: 'Status', type: 'select', required: true,
      options: Object.entries(ROCK_STATUS).map(([k, v]) => ({ value: k, label: v.label })),
      defaultValue: 'on_track',
    },
    { name: 'progress_pct', label: 'Voortgang', type: 'progress', defaultValue: 0 },
    { name: 'deadline', label: 'Deadline (bepaalt het kwartaal)', type: 'date' },
    { name: 'last_update_note', label: 'Laatste update-notitie', type: 'textarea', rows: 2 },
    { name: 'sort_order', label: 'Volgorde', type: 'number', defaultValue: 0 },
  ];
}
